import { getAPIClient } from "../helpers";
import type {
  ReservationFilters,
  CreateReservationData,
  UpdateReservationData,
  ReservationStatusUpdate,
  SeatReservationData,
  CreateCommentData,
  UpdateCommentData,
} from "../../api/routes/shop/routes/reservations";

type ReservationQueryParams = ReservationFilters & { date?: Date };

const getDateRangeForDay = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  const start = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  const end = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));

  return {
    date_from: start.toISOString(),
    date_to: end.toISOString(),
  };
};

export const reservationsAPI = {
  // GET /reservations - List reservations with filtering
  getReservations: async (
    filters?: ReservationQueryParams,
    signal?: AbortSignal,
  ) => {
    const hc = getAPIClient();
    const { date, ...rest } = filters || {};

    const query: ReservationFilters = {
      ...rest,
      ...(date && !rest.date_from && !rest.date_to
        ? getDateRangeForDay(date)
        : {}),
    };

    const resp = await hc.api.v2.reservations.$get(
      {
        query,
      },
      signal ? { init: { signal } } : undefined,
    );

    if (!resp.ok) {
      throw new Error(`Failed to fetch reservations: ${resp.status}`);
    }

    return await resp.json();
  },

  // GET /reservations/:id - Get single reservation
  getReservation: async (id: string) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations[":id"].$get({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error(`Failed to fetch reservation: ${resp.status}`);
    }

    return await resp.json();
  },

  // POST /reservations - Create new reservation
  createReservation: async (data: CreateReservationData) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations.$post({
      json: data,
    });

    if (!resp.ok) {
      throw new Error(`Failed to create reservation: ${resp.status}`);
    }

    return await resp.json();
  },

  // PUT /reservations/:id - Update reservation
  updateReservation: async (id: string, data: UpdateReservationData) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations[":id"].$put({
      param: { id },
      json: data,
    });

    if (!resp.ok) {
      throw new Error(`Failed to update reservation: ${resp.status}`);
    }

    return await resp.json();
  },

  // PATCH /reservations/:id/status - Update reservation status
  updateReservationStatus: async (
    id: string,
    statusUpdate: ReservationStatusUpdate,
  ) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations[":id"].status.$patch({
      param: { id },
      json: statusUpdate,
    });

    if (!resp.ok) {
      throw new Error(`Failed to update reservation status: ${resp.status}`);
    }

    return await resp.json();
  },

  // PUT /reservations/:id/tables - Update table assignments
  updateReservationTables: async (
    id: string,
    tableUpdate: { table_ids: number[] },
  ) => {
    const hc = getAPIClient();

    // Transform table_ids into table_assignments format expected by the API
    const table_assignments = (tableUpdate.table_ids || []).map((table_id) => ({
      table_id,
      status: "active" as const,
    }));

    const resp = await hc.api.v2.reservations[":id"].tables.$put({
      param: { id },
      json: { table_assignments },
    });

    if (!resp.ok) {
      throw new Error(`Failed to update reservation tables: ${resp.status}`);
    }

    return await resp.json();
  },

  // DELETE /reservations/:id - Delete reservation
  deleteReservation: async (id: string) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations[":id"].$delete({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error(`Failed to delete reservation: ${resp.status}`);
    }

    return await resp.json();
  },

  // POST /reservations/:id/seat - Seat a reservation at a table
  seatReservation: async (id: string, data: SeatReservationData) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations[":id"].seat.$post({
      param: { id },
      json: data,
    });

    if (!resp.ok) {
      throw new Error(`Failed to seat reservation: ${resp.status}`);
    }

    return await resp.json();
  },

  // POST /reservations/:id/cancel - Cancel a reservation
  cancelReservation: async (id: string) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations[":id"].cancel.$post({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error(`Failed to cancel reservation: ${resp.status}`);
    }

    return await resp.json();
  },

  // POST /reservations/:id/comments - Add a comment to a reservation
  createReservationComment: async (id: string, data: CreateCommentData) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations[":id"].comments.$post({
      param: { id },
      json: data,
    });

    if (!resp.ok) {
      throw new Error(`Failed to create reservation comment: ${resp.status}`);
    }

    return await resp.json();
  },

  // PUT /reservations/:id/comments/:commentId - Update a reservation comment
  updateReservationComment: async (
    id: string,
    commentId: string,
    data: UpdateCommentData,
  ) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservations[":id"].comments[
      ":commentId"
    ].$put({
      param: { id, commentId },
      json: data,
    });

    if (!resp.ok) {
      throw new Error(`Failed to update reservation comment: ${resp.status}`);
    }

    return await resp.json();
  },
};
